<script>
    import { createEventDispatcher } from "svelte";
    import Icon from "@iconify/svelte";
    import { hubAccessPin } from "@/assets/js/options";

    const dispatch = createEventDispatcher();
    const pinLength = hubAccessPin.length;

    let pin = "";
    let error = false;
    let keyRows = shuffleKeypad();

    function shuffleKeypad() {
        const digits = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
        for (let i = digits.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [digits[i], digits[j]] = [digits[j], digits[i]];
        }
        return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9), digits[9]];
    }

    export function reshuffle() {
        keyRows = shuffleKeypad();
        pin = "";
        error = false;
    }

    const reset = () => {
        pin = "";
        error = false;
    };

    const verify = () => {
        if (pin === hubAccessPin) {
            dispatch("success");
            reset();
            return;
        }
        error = true;
        pin = "";
        keyRows = shuffleKeypad();
    };

    const append = (digit) => {
        if (pin.length >= pinLength) return;
        pin = `${pin}${digit}`;
        if (pin.length === pinLength) verify();
    };

    const backspace = () => {
        pin = pin.slice(0, -1);
        error = false;
    };

    const close = () => {
        reset();
        dispatch("close");
    };
</script>

<div class="hub-pin col align-center">
    <button type="button" class="close-btn center" aria-label="Close" on:click={close}>
        <Icon icon="solar:close-circle-linear" width="26" />
    </button>

    <div class="icon-badge center">
        <Icon icon="solar:lock-keyhole-minimalistic-bold" width="28" color="#5be1e6" />
    </div>

    <div class="col gap-6 center header">
        <h2 class="font-22 weight-600">Hub access</h2>
        <p class="font-13 subtitle">Enter your PIN to continue</p>
    </div>

    <div class="pin-slots center" class:shake={error}>
        {#each Array(pinLength) as _, i}
            <span class="pin-slot" class:filled={i < pin.length}></span>
        {/each}
    </div>

    {#if error}
        <p class="error-banner row align-center gap-8 font-13">
            <Icon icon="solar:danger-circle-bold" width="18" />
            Incorrect PIN
        </p>
    {:else}
        <div class="error-spacer" />
    {/if}

    <div class="numpad col gap-10 w100">
        {#each keyRows.slice(0, 3) as row}
            <div class="row gap-10 justify-center">
                {#each row as digit}
                    <button type="button" class="key center" on:click={() => append(digit)}>{digit}</button>
                {/each}
            </div>
        {/each}
        <div class="row gap-10 justify-center">
            <button type="button" class="key key-action center" on:click={reset}>Clear</button>
            <button type="button" class="key center" on:click={() => append(keyRows[3])}>{keyRows[3]}</button>
            <button type="button" class="key key-action center" on:click={backspace} aria-label="Backspace">
                <Icon icon="solar:backspace-outline" width="22" />
            </button>
        </div>
    </div>
</div>

<style>
    .hub-pin {
        position: relative;
        width: min(100%, 340px);
        padding: 8px 4px 4px;
        gap: 22px;
    }

    .close-btn {
        position: absolute;
        top: -4px;
        right: -4px;
        width: 36px;
        height: 36px;
        border: none;
        background: rgba(255, 255, 255, 0.06);
        border-radius: 50%;
        color: rgba(255, 255, 255, 0.55);
        cursor: pointer;
        transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
    }

    .close-btn:hover {
        background: rgba(255, 255, 255, 0.12);
        color: white;
    }

    .close-btn:active {
        transform: scale(0.92);
    }

    .icon-badge {
        width: 56px;
        height: 56px;
        border-radius: 16px;
        background: linear-gradient(145deg, rgba(91, 225, 230, 0.22), rgba(91, 225, 230, 0.06));
        border: 1px solid rgba(91, 225, 230, 0.35);
        box-shadow: 0 0 24px rgba(91, 225, 230, 0.15);
    }

    .header {
        text-align: center;
    }

    .subtitle {
        color: rgba(255, 255, 255, 0.45);
    }

    .pin-slots {
        display: flex;
        gap: 22px;
        min-height: 52px;
    }

    .pin-slot {
        flex-shrink: 0;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.08);
        border: 1.5px solid rgba(255, 255, 255, 0.2);
        transition:
            transform 0.2s cubic-bezier(0.34, 1.4, 0.64, 1),
            background 0.2s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease;
    }

    .pin-slot.filled {
        background: var(--primary, #5be1e6);
        border-color: var(--primary, #5be1e6);
        box-shadow: 0 0 12px rgba(91, 225, 230, 0.45);
    }

    .error-spacer {
        height: 20px;
    }

    .error-banner {
        color: #ff6b6b;
        background: rgba(255, 107, 107, 0.12);
        border: 1px solid rgba(255, 107, 107, 0.25);
        border-radius: 10px;
        padding: 8px 14px;
        margin: -6px 0;
    }

    .numpad {
        margin-top: 4px;
    }

    .key {
        width: 76px;
        height: 76px;
        font-size: 26px;
        font-weight: 500;
        border-radius: 18px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        background: linear-gradient(160deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.03));
        color: white;
        cursor: pointer;
        transition:
            transform 0.12s ease,
            background 0.15s ease,
            border-color 0.15s ease,
            box-shadow 0.15s ease;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }

    .key:active {
        transform: scale(0.94);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    }

    .key-action {
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: rgba(255, 255, 255, 0.65);
        background: linear-gradient(160deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
    }

    .shake {
        animation: shake 0.45s ease;
    }

    @keyframes shake {
        0%,
        100% {
            transform: translateX(0);
        }
        20% {
            transform: translateX(-10px);
        }
        40% {
            transform: translateX(10px);
        }
        60% {
            transform: translateX(-6px);
        }
        80% {
            transform: translateX(6px);
        }
    }

    @media (max-width: 400px) {
        .key {
            width: 64px;
            height: 64px;
            font-size: 22px;
            border-radius: 14px;
        }
    }
</style>
